import { useNotify } from '@globaleris/ui.library'
import { Box, CircularProgress, TextField, Typography } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AtlasUserModel } from '../../../../../api/Argentia/endpoints/tasks/model/tasks'
import { EnumEstadoUsuarioAtlas } from '../../../../../api/oData/enums/enumEstadoUsuarioAtlas'
import SimpleDialog from '../../../../../components/shared/simpleDialog/simpleDialog'
import { debounce } from '../../../../../helpers/debounceHelper'
import { sanitizeQuery } from '../../../../../helpers/sanitizeQueryHelper'
import { useConfirmationDialog } from '../../../../../hooks'
import { ConfirmationDialogs } from '../../../../../model/enums/confirmationDialogs'
import { addAtlasSuperUser, searchAtlasUsers } from '../../atlasAccess.service'

// query mínima para no disparar la búsqueda de usuarios en cada tecleo
const MIN_USER_QUERY_LENGTH = 3

interface AddSuperUserDialogProps {
  open: boolean
  onClose: () => void
  // se llama luego de añadir el superusuario con éxito, para refrescar tabla + KPIs
  onSuccess: () => void
}

export const AddSuperUserDialog: React.FC<AddSuperUserDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'atlasAccess'])
  const { error, info } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [userOptions, setUserOptions] = useState<AtlasUserModel[]>([])
  const [isFetchingUsers, setIsFetchingUsers] = useState<boolean>(false)
  const [selectedUser, setSelectedUser] = useState<AtlasUserModel | undefined>(undefined)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  // #region Functions Starts
  const searchUserOptions = async (query: string) => {
    setIsFetchingUsers(true)
    await searchAtlasUsers(query)
      .then(options => setUserOptions(options ?? []))
      .finally(() => setIsFetchingUsers(false))
  }

  const debouncedSearchUserOptions = useCallback(debounce(searchUserOptions, 500), [])

  const isUserOptionDisabled = (option: AtlasUserModel): boolean =>
    option.superuser || option.estado === EnumEstadoUsuarioAtlas.Baja
  // #endregion Functions Ends

  // #region Handlers Starts
  const handleUserInputChange = (event: React.ChangeEvent<{}>, value: string, reason: string) => {
    if (reason !== 'input') {
      return
    }
    const query = sanitizeQuery(value, ['@'])
    if (query.trim().length < MIN_USER_QUERY_LENGTH) {
      setUserOptions([])
      return
    }
    debouncedSearchUserOptions(query)
  }

  const handleClose = () => {
    setSelectedUser(undefined)
    setUserOptions([])
    onClose()
  }

  const handleAdd = async () => {
    if (!selectedUser) {
      return
    }
    await getConfirmation(ConfirmationDialogs.ADD_ATLAS_SUPERUSER).then(async () => {
      setIsSaving(true)
      await addAtlasSuperUser(selectedUser.id)
        .then(response => {
          if (response.success) {
            info(t('atlasAccess:components.addSuperUserDialog.notifications.success'))
            handleClose()
            onSuccess()
          } else {
            error(t('atlasAccess:components.addSuperUserDialog.notifications.error'))
          }
        })
        .finally(() => setIsSaving(false))
    })
    closeConfirmation()
  }
  // #endregion Handlers Ends

  return (
    <SimpleDialog
      open={open}
      handleClose={handleClose}
      title={t('atlasAccess:components.addSuperUserDialog.title')}
      footerActions={[
        {
          label: t('atlasAccess:components.addSuperUserDialog.buttons.cancel'),
          handleActionClick: handleClose
        },
        {
          label: t('atlasAccess:components.addSuperUserDialog.buttons.add'),
          color: 'primary',
          variant: 'contained',
          disabled: !selectedUser || isSaving,
          handleActionClick: handleAdd
        }
      ]}
    >
      <Typography variant="body2" gutterBottom>
        {t('atlasAccess:components.addSuperUserDialog.description')}
      </Typography>
      <Box mt={2}>
        <Autocomplete
          style={{ width: '100%' }}
          value={selectedUser ?? null}
          options={userOptions}
          loading={isFetchingUsers}
          filterOptions={options => options}
          getOptionLabel={(option: AtlasUserModel) => `${option.authorNameAtlas} (${option.email})`}
          getOptionSelected={(option: AtlasUserModel, value: AtlasUserModel) =>
            option.id === value.id
          }
          getOptionDisabled={isUserOptionDisabled}
          onChange={(event, option) => setSelectedUser(option ?? undefined)}
          onInputChange={handleUserInputChange}
          noOptionsText={t('atlasAccess:components.addSuperUserDialog.autocomplete.noOptions')}
          loadingText={t('atlasAccess:components.addSuperUserDialog.autocomplete.loading')}
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              variant="outlined"
              label={t('atlasAccess:components.addSuperUserDialog.inputs.user')}
              placeholder={t('atlasAccess:components.addSuperUserDialog.placeholders.user')}
              helperText={t('atlasAccess:components.addSuperUserDialog.helpText.alreadySuperUserOrBaja')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {isFetchingUsers ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                )
              }}
            />
          )}
        />
      </Box>
    </SimpleDialog>
  )
}
